import React, { useState } from "react";
import { FiFilter, FiChevronDown, FiLoader } from "react-icons/fi";
import categoryAPI from "../../../api/categoryApi";

// The flat GET /categories listing only returns top-level categories —
// subcategories aren't embedded on each item. If one IS embedded (some
// other caller passed a richer object), use it straightaway; otherwise
// subcategories are fetched lazily, per category, the first time its
// accordion is opened — via GET /categories/:slug, the same endpoint the
// Category Detail page uses (see pages/categories/DetailPage.js ->
// res.data.subcategories).
const getEmbeddedSubcategories = (cat) =>
  cat?.children || cat?.subCategories || cat?.subcategories || null;

function Filtersidebar({
  filterData,
  categories, // real category + subcategory tree from CategoryContext
  filters,
  onFilterChange,
  onCheckboxChange,
  priceRange,
  onPriceChange,
  onApply,
}) {
  // Prefer the dedicated categories prop (from CategoryContext); fall back to
  // filterData.categories only if the caller hasn't passed `categories` yet.
  const categoryList = categories?.length ? categories : filterData?.categories;
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllBrands, setShowAllBrands] = useState(false);
  // Tracks which parent category's accordion is open. Only one open at a time;
  // change to an object/array if you want multiple open together.
  const [openCategoryId, setOpenCategoryId] = useState(null);
  // Per-category subcategory cache: { [categoryId]: subcategoryArray }.
  // Once a category's subcategories are fetched they're kept here so
  // re-opening the accordion doesn't re-hit the API.
  const [subCategoryCache, setSubCategoryCache] = useState({});
  const [loadingCategoryId, setLoadingCategoryId] = useState(null);

  const toggleCategoryAccordion = async (cat) => {
    const id = cat._id;

    // Already open -> just collapse it.
    if (openCategoryId === id) {
      setOpenCategoryId(null);
      return;
    }

    setOpenCategoryId(id);

    // Already fetched (or embedded on the object itself) — nothing to do.
    if (subCategoryCache[id] !== undefined) return;

    const embedded = getEmbeddedSubcategories(cat);
    if (embedded) {
      setSubCategoryCache((prev) => ({ ...prev, [id]: embedded }));
      return;
    }

    setLoadingCategoryId(id);

    try {
      const slug = cat.slug || cat.slugPath;
      const res = await categoryAPI.details(slug);

      setSubCategoryCache((prev) => ({
        ...prev,
        [id]: res.code === 200 ? res.data?.subcategories || [] : [],
      }));
    } catch (err) {
      console.error("Error loading subcategories:", err);
      setSubCategoryCache((prev) => ({ ...prev, [id]: [] }));
    } finally {
      setLoadingCategoryId(null);
    }
  };

  const minLimit = filterData?.priceRange?.min || 0;
  const maxLimit = filterData?.priceRange?.max || 10000;
  
  const minVal = priceRange[0];
  const maxVal = priceRange[1];

  const minPercent = ((minVal - minLimit) / (maxLimit - minLimit)) * 100;
  const maxPercent = ((maxVal - minLimit) / (maxLimit - minLimit)) * 100;

  return (
    <div className="bg-[#FFFDF8] text-[#2E211A] p-6 rounded-2xl border border-[#E5D9C3] shadow-[0_4px_12px_rgba(74,47,30,0.16)] transition-all duration-300">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 mb-6 border-b border-[#E5D9C3]">
        <h5 className="text-[1.25rem] font-bold text-[#4B3226] flex items-center gap-2 m-0 tracking-wide">
          <FiFilter className="text-[#A6472E]" size={20} /> Filters
        </h5>
        <button
          onClick={onApply}
          className="lg:hidden px-4 py-1.5 bg-[#A6472E] text-white text-xs font-semibold rounded-lg shadow-sm hover:bg-[#7A2F1E] active:scale-95 transition-all duration-150"
        >
          Apply
        </button>
      </div>

      {/* CATEGORY */}
      <div className="mb-6">
        <label className="block text-xs font-bold tracking-wider text-[#6B5D53] uppercase mb-3">
          Category
        </label>
        <div className="hide-scrollbar-lg space-y-1.5 max-h-72 overflow-y-auto pr-1 custom-scrollbar">
          {(showAllCategories
            ? categoryList
            : categoryList?.slice(0, 7)
          )?.map((cat) => {
            const isChecked = filters.category.includes(cat._id);
            const isOpen = openCategoryId === cat._id;
            const isLoadingSubs = loadingCategoryId === cat._id;
            const subCats =
              subCategoryCache[cat._id] ?? getEmbeddedSubcategories(cat) ?? [];
            // We don't know if a category has subcategories until we've
            // fetched (or it came embedded) — so the "no subcategories"
            // empty state only shows once that fetch has actually resolved.
            const subsResolved = subCategoryCache[cat._id] !== undefined;

            return (
              <div key={cat._id} className="rounded-xl overflow-hidden">
                {/* Parent row: checkbox + name (click) + chevron (click to expand) */}
                <div
                  className={`flex items-center justify-between gap-2 p-2 rounded-xl transition-all duration-200 ${
                    isChecked ? "bg-[#F6EFE2] text-[#A6472E] font-medium" : "hover:bg-[#F6EFE2]/50 text-[#2E211A]"
                  }`}
                >
                  <label
                    htmlFor={`cat-${cat._id}`}
                    className="flex items-center gap-3 flex-1 cursor-pointer min-w-0"
                  >
                    <input
                      type="checkbox"
                      name="category"
                      id={`cat-${cat._id}`}
                      value={cat._id}
                      checked={isChecked}
                      onChange={() => onCheckboxChange("category", cat._id)}
                      className="w-4 h-4 accent-[#A6472E] rounded cursor-pointer flex-shrink-0"
                    />
                    <span className="text-sm select-none truncate">{cat.name}</span>
                  </label>

                  <button
                    type="button"
                    onClick={() => toggleCategoryAccordion(cat)}
                    aria-expanded={isOpen}
                    aria-label={`Toggle ${cat.name} subcategories`}
                    className="p-1 rounded-full text-[#6B5D53] hover:text-[#A6472E] hover:bg-[#F6EFE2] transition-colors flex-shrink-0"
                  >
                    {isLoadingSubs ? (
                      <FiLoader size={16} className="animate-spin" />
                    ) : (
                      <FiChevronDown
                        size={16}
                        style={{
                          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.25s ease",
                        }}
                      />
                    )}
                  </button>
                </div>

                {/* Subcategories - accordion, opens on click, fetched lazily */}
                <div
                  style={{
                    maxHeight: isOpen
                      ? `${Math.max(subCats.length, 1) * 42 + 8}px`
                      : "0px",
                    transition: "max-height 0.3s ease",
                  }}
                  className="overflow-hidden pl-6 border-l border-[#E5D9C3] ml-4"
                >
                  <div className="py-1 space-y-1">
                    {isLoadingSubs && (
                      <p className="text-xs text-[#6B5D53] py-1.5 px-2">
                        Loading...
                      </p>
                    )}

                    {!isLoadingSubs && subsResolved && subCats.length === 0 && (
                      <p className="text-xs text-[#6B5D53] py-1.5 px-2">
                        No subcategories
                      </p>
                    )}

                    {!isLoadingSubs &&
                      subCats.map((sub) => {
                        const isSubChecked = filters.category.includes(sub._id);
                        return (
                          <label
                            key={sub._id}
                            htmlFor={`subcat-${sub._id}`}
                            className={`flex items-center gap-3 py-1.5 px-2 rounded-lg cursor-pointer transition-all duration-150 ${
                              isSubChecked
                                ? "text-[#A6472E] font-medium bg-[#F6EFE2]/70"
                                : "text-[#6B5D53] hover:text-[#2E211A] hover:bg-[#F6EFE2]/40"
                            }`}
                          >
                            <input
                              type="checkbox"
                              name="category"
                              id={`subcat-${sub._id}`}
                              value={sub._id}
                              checked={isSubChecked}
                              onChange={() => onCheckboxChange("category", sub._id)}
                              className="w-3.5 h-3.5 accent-[#A6472E] rounded cursor-pointer flex-shrink-0"
                            />
                            <span className="text-xs select-none truncate">{sub.name}</span>
                          </label>
                        );
                      })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {categoryList?.length > 7 && (
          <button
            type="button"
            onClick={() => setShowAllCategories((prev) => !prev)}
            className="mt-3 text-xs font-semibold text-[#A6472E] hover:text-[#7A2F1E] transition-colors"
          >
            {showAllCategories ? "View Less" : "View More"}
          </button>
        )}
      </div>

      {/* BRAND */}
      <div className="mb-6">
        <label className="block text-xs font-bold tracking-wider text-[#6B5D53] uppercase mb-3">
          Brand
        </label>
        <div className="space-y-2.5 max-h-56 overflow-y-auto pr-1 custom-scrollbar">
          {(showAllBrands
            ? filterData?.brands
            : filterData?.brands?.slice(0, 7)
          )?.map((b) => {
            const isChecked = filters.brand.includes(b.id);
            return (
              <label
                key={b.id}
                htmlFor={`brand-${b.id}`}
                className={`flex items-center gap-3 p-2 rounded-xl cursor-pointer transition-all duration-200 ${
                  isChecked ? "bg-[#F6EFE2] text-[#A6472E] font-medium" : "hover:bg-[#F6EFE2]/50 text-[#2E211A]"
                }`}
              >
                <input
                  type="checkbox"
                  name="brand"
                  id={`brand-${b.id}`}
                  value={b.id}
                  checked={isChecked}
                  onChange={() => onCheckboxChange("brand", b.id)}
                  className="w-4 h-4 accent-[#A6472E] rounded cursor-pointer"
                />
                <span className="text-sm select-none">{b.name}</span>
              </label>
            );
          })}
        </div>
        {filterData?.brands?.length > 7 && (
          <button
            type="button"
            onClick={() => setShowAllBrands((prev) => !prev)}
            className="mt-3 text-xs font-semibold text-[#A6472E] hover:text-[#7A2F1E] transition-colors"
          >
            {showAllBrands ? "View Less" : "View More"}
          </button>
        )}
      </div>

      {/* PRICE RANGE */}
      <div className="mb-6 px-1">
        <label className="block text-xs font-bold tracking-wider text-[#6B5D53] uppercase mb-4">
          Price Range
        </label>
        
        <div className="relative h-6 flex items-center mb-3">
          <div className="absolute w-full h-1.5 bg-[#E5D9C3] rounded-full"></div>
          <div
            className="absolute h-1.5 bg-[#A6472E] rounded-full transition-all duration-75"
            style={{
              left: `${minPercent}%`,
              right: `${100 - maxPercent}%`,
            }}
          ></div>
          
          <input
            type="range"
            min={minLimit}
            max={maxLimit}
            value={minVal}
            onChange={(e) => {
              const val = Math.min(Number(e.target.value), maxVal - 100);
              onPriceChange([val, maxVal]);
            }}
            className="absolute w-full appearance-none bg-transparent pointer-events-none accent-[#A6472E] [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#A6472E] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer"
          />
          <input
            type="range"
            min={minLimit}
            max={maxLimit}
            value={maxVal}
            onChange={(e) => {
              const val = Math.max(Number(e.target.value), minVal + 100);
              onPriceChange([minVal, val]);
            }}
            className="absolute w-full appearance-none bg-transparent pointer-events-none accent-[#A6472E] [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#A6472E] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer"
          />
        </div>

        <div className="flex items-center justify-between text-xs font-semibold text-[#4B3226] bg-[#F6EFE2] px-3 py-2 rounded-xl border border-[#E5D9C3]">
          <span>₹{minVal}</span>
          <span className="text-[#6B5D53]">-</span>
          <span>₹{maxVal}+</span>
        </div>
      </div>

      {/* SORT */}
      <div>
        <label className="block text-xs font-bold tracking-wider text-[#6B5D53] uppercase mb-3">
          Sort By
        </label>
        <div className="space-y-2.5">
          {filterData?.sorting?.map((opt, i) => {
            const isChecked = filters.sort === opt.value;
            return (
              <label
                key={i}
                htmlFor={`sort-${i}`}
                className={`flex items-center gap-3 p-2 rounded-xl cursor-pointer transition-all duration-200 ${
                  isChecked ? "bg-[#F6EFE2] text-[#A6472E] font-medium" : "hover:bg-[#F6EFE2]/50 text-[#2E211A]"
                }`}
              >
                <input
                  type="checkbox"
                  name="sort"
                  id={`sort-${i}`}
                  value={opt.value}
                  checked={isChecked}
                  onChange={(e) => {
                    if (filters.sort === opt.value) {
                      onFilterChange("sort", "");
                    } else {
                      onFilterChange("sort", e.target.value);
                    }
                  }}
                  className="w-4 h-4 accent-[#A6472E] rounded cursor-pointer"
                />
                <span className="text-sm select-none">{opt.label}</span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Filtersidebar;